import { addQueryParamsToUrl } from "http-with-fetch";
import { Syringe } from "@faizaanceg/syringe";

const DEFAULT_EMBASSY_BASE = "/embassy/superpanel/";

const Endpoints = (embassyBase: string) => ({
  LOGIN: `${embassyBase}login`,
  VISA: `${embassyBase}visa`,
  REFRESH: `${embassyBase}refresh`,
  LOGOUT: `${embassyBase}logout`
});

const init: RequestInit = { credentials: "include" };

export function normalizeEmbassyBase(base: string): string {
  if (!base) return DEFAULT_EMBASSY_BASE;
  return base.endsWith("/") ? base : `${base}/`;
}

export function getEmbassyBase(
  settings?: Pick<RuntimeSettings, "embassy"> | null
): string {
  if (settings) {
    return normalizeEmbassyBase(settings.embassy ?? DEFAULT_EMBASSY_BASE);
  }
  const runtime = Syringe.inject("runtime") as RuntimeSettings | null;
  if (runtime) {
    return normalizeEmbassyBase(runtime.embassy ?? DEFAULT_EMBASSY_BASE);
  }
  return DEFAULT_EMBASSY_BASE;
}

/** @deprecated Use embassy base path from runtime; namespace-only paths are legacy. */
export function embassyBaseFromNamespace(namespace: string): string {
  return normalizeEmbassyBase(`/embassy/${namespace}/`);
}

export class SessionSdk {
  private endpoints: Record<string, string>;
  constructor(embassyBase?: string) {
    this.endpoints = Endpoints(
      normalizeEmbassyBase(embassyBase ?? DEFAULT_EMBASSY_BASE)
    );
  }
  async create(token: string) {
    const response = await fetch(
      addQueryParamsToUrl(this.endpoints.LOGIN, {
        auth_code: token,
        issue_redirect: false
      })
    );
    if (!response.ok) {
      throw new Error("Session is not established");
    }
  }
  async visa(refresh: boolean): Promise<{ active: boolean } & Session> {
    let endpoint = refresh ? this.endpoints.REFRESH : this.endpoints.VISA;
    return fetch(endpoint, init).then(
      res => res.json(),
      () => ({ active: false })
    );
  }
  async logout() {
    return fetch(this.endpoints.LOGOUT, { ...init, method: "DELETE" });
  }
}

export function createEmbassySession(_namespace?: string) {
  return async function EmbassySession(isRefreshNeeded: boolean) {
    const session = new SessionSdk(getEmbassyBase());
    let response = await session.visa(false);
    if (!response.active && isRefreshNeeded) {
      response = await session.visa(true);
    }
    return response.active ? response : null;
  };
}

export async function logoutEmbassySession(
  settings: Pick<RuntimeSettings, "embassy">
): Promise<void> {
  const session = new SessionSdk(getEmbassyBase(settings));
  await session.logout();
}
